import React, { useEffect, useState } from 'react';
import ComponentName from '../../constant/ComponentName';
import VariableType from '../../constant/VariableType';
import { ChannelUndeployDependent } from '../../domain/stream/transaction/ChannelUndeployDependent';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import ComponentService from '../../service/ComponentService';
import LanguageUtil from '../../util/LanguageUtil';
import MessageUtil from '../../util/MessageUtil';
import ChannelUndeployImpactDialog from '../transaction/ChannelUndeployImpactDialog';

interface PendingUndeploy {
  dependents: ChannelUndeployDependent[];
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

let publishPending: ((pending: PendingUndeploy | undefined) => void) | undefined;

const readComponentService = (): ComponentService => {
  return ServiceContainer.getInstance().get(ServiceNames.COMPONENT);
};

export function enrichmentUndeployComponent(type: string): ComponentName {
  switch (type) {
    case VariableType.VELOCITY:
      return ComponentName.VELOCITY;
    case VariableType.QUERY:
      return ComponentName.QUERY_VARIABLE;
    case VariableType.API:
      return ComponentName.API_VARIABLE;
    case VariableType.LIST:
      return ComponentName.LIST_VARIABLE;
    case VariableType.BASIC:
      return ComponentName.BASIC_VARIABLE;
    default:
      return ComponentName.ENRICHMENT;
  }
}

export async function confirmUndeployWithImpact(component: ComponentName, ids: string[], proceed: () => Promise<void> | void): Promise<void> {
  const names = ids.filter((id) => id != undefined && id.trim().length > 0);
  if (names.length === 0) {
    await proceed();
    return;
  }
  let dependents: ChannelUndeployDependent[] = [];
  try {
    dependents = await readComponentService().listUndeployDependents(component, names);
  } catch {
    MessageUtil.error({
      summary: LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeploy' }),
      detail: LanguageUtil.get({
        id: 'messages.error.undeployDependentsCheckFailed',
        defaultMessage: 'Bound components could not be listed. Undeploy was cancelled.',
      }),
    });
    return;
  }
  if (dependents == undefined || dependents.length === 0) {
    await proceed();
    return;
  }
  await new Promise<void>((resolve) => {
    if (publishPending == undefined) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeploy' }),
        detail: LanguageUtil.get({
          id: 'messages.error.undeployConfirmUnavailable',
          defaultMessage: 'Undeploy confirmation could not be opened. Undeploy was cancelled. Please refresh the page.',
        }),
      });
      resolve();
      return;
    }
    publishPending({
      dependents,
      onConfirm: async () => {
        try {
          await proceed();
        } finally {
          resolve();
        }
      },
      onCancel: () => resolve(),
    });
  });
}

const UndeployImpactDialog = () => {
  const [pending, setPending] = useState<PendingUndeploy | undefined>(undefined);

  useEffect(() => {
    publishPending = setPending;
    return () => {
      publishPending = undefined;
    };
  }, []);

  const close = () => {
    setPending(undefined);
  };

  return (
    <ChannelUndeployImpactDialog
      visible={pending != undefined}
      dependents={pending?.dependents ?? []}
      onCancel={() => {
        pending?.onCancel();
        close();
      }}
      onConfirm={async () => {
        const current = pending;
        close();
        await current?.onConfirm();
      }}
    />
  );
};

export default UndeployImpactDialog;
